import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Check, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/layout/AppLayout';
import { RequireBusiness } from '@/components/RequireBusiness';
import { useApp } from '@/contexts/AppContext';
import { useState } from 'react';
import { getSafeGoogleUrl } from '@/lib/urlValidation';

interface ReviewFromState {
  id: string;
  text: string;
  category: string;
}

export default function PostReviewPage() {
  return (
    <RequireBusiness>
      <PostReviewContent />
    </RequireBusiness>
  );
}

function PostReviewContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { business, markAsSubmitted } = useApp();
  const review = location.state?.review as ReviewFromState | undefined;
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (review) {
      navigator.clipboard.writeText(review.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleMarkSubmitted = async () => {
    if (review?.id && !review.id.startsWith('temp-')) {
      await markAsSubmitted(review.id);
    }
    navigate('/history');
  };

  if (!review) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto text-center py-12">
          <p className="text-muted-foreground">No review selected</p>
          <Button onClick={() => navigate('/generate')} className="mt-4">
            Generate a Review
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/generate')}
            className="p-2 hover:bg-muted rounded-md transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-semibold">Post Review to Google</h1>
        </div>

        {/* Instructions Banner */}
        <div className="wireframe-card mb-6 bg-muted/50">
          <h2 className="font-medium mb-2">Instructions</h2>
          <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
            <li>Copy the review text using the button below</li>
            <li>Click "Open Google Reviews" to go to your business profile</li>
            <li>Sign in with your customer's Google account</li>
            <li>Paste the review and submit</li>
            <li>Return here and click "Mark as Submitted"</li>
          </ol>
        </div>

        {/* Split View */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel - Review */}
          <div className="wireframe-card">
            <h2 className="text-lg font-medium mb-4">Selected Review</h2>
            
            {/* Category Label */}
            <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded inline-block mb-3">
              {review.category}
            </span>
            
            {/* Review Text */}
            <div className="border border-border rounded-lg p-4 mb-4 bg-muted/30">
              <p className="text-sm leading-relaxed">{review.text}</p>
            </div>

            {/* Copy Button */}
            <Button
              onClick={handleCopy}
              variant="outline"
              className="w-full"
            >
              {copied ? (
                <>
                  <Check className="mr-2" size={16} />
                  Copied to Clipboard!
                </>
              ) : (
                <>
                  <Copy className="mr-2" size={16} />
                  Copy Review Text
                </>
              )}
            </Button>
          </div>

          {/* Right Panel - Google Embed Placeholder */}
          <div className="wireframe-card">
            <h2 className="text-lg font-medium mb-4">Google Business Profile</h2>
            
            {/* Embed Placeholder */}
            <div className="border-2 border-dashed border-border rounded-lg h-64 flex flex-col items-center justify-center mb-4">
              <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center mb-3">
                <ExternalLink size={24} className="text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground text-center px-4">
                Google Business Profile review page will open in a new tab
              </p>
            </div>

            {/* Open Google Button */}
            <Button
              onClick={() => window.open(getSafeGoogleUrl(business?.google_profile_url), '_blank')}
              className="w-full mb-4"
            >
              <ExternalLink className="mr-2" size={16} />
              Open Google Reviews
            </Button>

            {/* Mark as Submitted */}
            <Button
              onClick={handleMarkSubmitted}
              variant="outline"
              className="w-full"
            >
              <Check className="mr-2" size={16} />
              Mark as Submitted
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
